// Utils and Service
import { getErrorMessage, isCanceledError } from '../utils/helpers'

import axios from './instance'

/**
 * Creates a repair order using the specified `payload`.
 * @param {string} dealerId
 * @param {string} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createRepairOrder = async (
  dealerId,
  payload,
  setLoading,
  setError,
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.post(`/dealers/${dealerId}/repair-orders/`, payload)

    setLoading(false)
    setSuccess('Repair order created.')
    return response
  } catch (err) {
    if (err.response?.data?.roId) {
      setError({
        message: err.response.data.results[0],
        roId: err.response.data.roId,
      })
    } else {
      setError(getErrorMessage(err))
    }

    setLoading(false)
    return null
  }
}

// URLs carry one `ro_number` param per number, so a very large paste is asked for in
// chunks rather than in one request the server may refuse on length.
const RO_NUMBER_LOOKUP_CHUNK_SIZE = 150

/**
 * Returns which of `roNumbers` this dealer already has, for the Log ROs preview's
 * duplicate check. Only the numbers come back, not whole repair orders.
 *
 * Soft-deleted ROs are excluded by the endpoint, which matches the partial unique
 * constraint — a removed RO does not block re-logging its number.
 * @param {string} dealerId
 * @param {string[]} roNumbers
 * @param {func} setError
 * @param {AbortSignal} signal
 * @returns {Promise<string[]>} the subset that already exists, uppercased
 */
export const getExistingRoNumbers = async (dealerId, roNumbers, setError, signal = undefined) => {
  if (roNumbers.length === 0) return []

  const chunks = []
  for (let i = 0; i < roNumbers.length; i += RO_NUMBER_LOOKUP_CHUNK_SIZE) {
    chunks.push(roNumbers.slice(i, i + RO_NUMBER_LOOKUP_CHUNK_SIZE))
  }

  try {
    const responses = await Promise.all(
      chunks.map((chunk) => {
        const params = new URLSearchParams()
        chunk.forEach((roNumber) => params.append('ro_number', roNumber))
        // No `fields=` here: FlexFieldsFilterBackend narrows the queryset to match,
        // which collides with the viewset's own select_related/annotations and 500s.
        params.append('limit', String(chunk.length))

        return axios.get(`/dealers/${dealerId}/repair-orders/?${params.toString()}`, { signal })
      }),
    )

    return responses.flatMap((response) => (response?.results || []).map((ro) => ro.roNumber))
  } catch (err) {
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * Counts per work bucket per period for one dealer, backing the RO status dashboard.
 * One request for the whole matrix rather than one per cell.
 * @param {string} dealerId
 * @param {func} setError
 * @param {AbortSignal} signal
 */
export const getRoStatusSummary = async (dealerId, setError, signal = undefined) => {
  try {
    return await axios.get(`/dealers/${dealerId}/repair-orders/ro-status-summary/`, { signal })
  } catch (err) {
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}

// `filter` is a dashboard bucket's query: `{ statuses, previouslySubmitted, olStates,
// excludeOlStates }` (see RoStatusDashboard/buckets.js). An empty status list means any
// status.
const roStatusParams = (
  range,
  { statuses = [], previouslySubmitted, olStates = [], excludeOlStates = [] } = {},
) => {
  const params = new URLSearchParams()
  params.append('received_at__gte', range.from)
  params.append('received_at__lte', range.to)
  statuses.forEach((status) => params.append('status', status))
  olStates.forEach((state) => params.append('state', state))
  excludeOlStates.forEach((state) => params.append('exclude_ol_state', state))
  if (previouslySubmitted !== undefined) {
    params.append('previously_submitted', previouslySubmitted ? 'true' : 'false')
  }
  return params
}

/**
 * One page of the ROs behind a dashboard cell (WARP-943).
 * @param {string} dealerId
 * @param {{from: string, to: string}} range - received dates, `YYYY-MM-DD`, inclusive
 * @param {{statuses: string[], previouslySubmitted?: boolean}} filter - the bucket's query
 * @param {{page: number, pageSize: number, orderBy: string}} query - `orderBy` is an API
 *   ordering field, `-` prefixed for descending
 */
export const getRoStatusList = async (
  dealerId,
  range,
  filter,
  { page = 1, pageSize = 50, orderBy = 'received_at' } = {},
  setError = () => {},
  signal = undefined,
) => {
  const params = roStatusParams(range, filter)
  params.append('order_by', orderBy)
  params.append('page', String(page))
  params.append('limit', String(pageSize))
  // Line items and their actions needed: the panel shows open actions as each RO's note,
  // and written-off line items as the dollars lost.
  params.append('expand', 'line_items,line_items.actions_needed,line_items.actions_needed.action')

  try {
    return await axios.get(`/dealers/${dealerId}/repair-orders/?${params.toString()}`, { signal })
  } catch (err) {
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * How many ROs match, without loading them: a one-row page, read for its `count`.
 * Used for the aging strip, which has to count the whole cell, not the page on screen.
 */
export const getRoStatusCount = async (dealerId, range, filter, signal = undefined) => {
  const params = roStatusParams(range, filter)
  params.append('limit', '1')
  try {
    const response = await axios.get(`/dealers/${dealerId}/repair-orders/?${params.toString()}`, {
      signal,
    })
    return response?.count ?? null
  } catch (err) {
    return null
  }
}

/**
 * Uploads a file to a repair order.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {FormData} formData
 */
export const createRepairOrderFile = async (dealerId, repairOrderId, formData) => {
  const response = await axios.post(
    `/dealers/${dealerId}/repair-orders/${repairOrderId}/files/`,
    formData,
  )
  return response
}

export const deleteRepairOrderFile = async (dealerId, repairOrderId, fileId) => {
  await axios.delete(`/dealers/${dealerId}/repair-orders/${repairOrderId}/files/${fileId}/`)
}

/**
 * Creates a repair order using the specified `payload`.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {func} setLoading
 * @param {func} setError
 */
export const getRepairOrder = async (dealerId, repairOrderId, setLoading, setError) => {
  setLoading(true)

  try {
    const response = await axios.get(
      `/dealers/${dealerId}/repair-orders/${repairOrderId}/?expand=line_items,line_items.files,line_items.actions_needed,line_items.actions_needed.action,line_items.written_off_by`,
    )

    setLoading(false)
    return response
  } catch (err) {
    const error = getErrorMessage(err)

    // Session expiry was already surfaced by the response interceptor;
    // getErrorMessage returns null specifically in that case. Bail without a
    // duplicate toast and without crashing on the null .includes checks below,
    // clearing loading so callers don't strand a spinner.
    if (error === null) {
      setLoading(false)
      return null
    }

    if (
      error.includes('given query') ||
      error.includes('%(value)s') ||
      error.includes('Not found')
    ) {
      setError('Repair Order not found.')
    } else {
      setError(error)
    }
    setLoading(false)
    return null
  }
}

/**
 * Gets the Repair Orders Advisors for the specified 'dealer_id`.
 * @param {string} dealerId
 * @param {func} setLoading
 * @param {func} setError
 * @returns `response` results
 */
export const getRepairOrderAdvisors = async (
  dealerId,
  setLoading = () => {},
  setError = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${dealerId}/repair-orders/advisors/`)
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the Repair Orders Technicians for the specified 'dealer_id`.
 * @param {string} dealerId
 * @param {func} setLoading
 * @param {func} setError
 * @returns `response` results
 */
export const getRepairOrderTechnicians = async (
  dealerId,
  setLoading = () => {},
  setError = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.get(`/dealers/${dealerId}/repair-orders/technicians/`)
    setLoading(false)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Update repair order.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const updateRepairOrder = async (
  dealerId,
  repairOrderId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.patch(
      `/dealers/${dealerId}/repair-orders/${repairOrderId}/`,
      payload,
    )

    setLoading(false)
    setSuccess('Repair order updated.')
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Undo putting an RO Intake order on the Outstanding List, returning it to a clean
 * intake state. OL-stage data (line items, comments) is cleared while the audit log
 * is preserved. Backend gates this to RO Intake dealers.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const returnRepairOrderToIntake = async (
  dealerId,
  repairOrderId,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.patch(`/dealers/${dealerId}/repair-orders/${repairOrderId}/`, {
      returnToIntake: true,
    })

    setLoading(false)
    setSuccess('Repair order returned to intake.')
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the comments for a repair order.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {number} page
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 *
 */
export const getComments = async (
  dealerId,
  repairOrderId,
  cursor = null,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    let url = `/dealers/${dealerId}/repair-orders/${repairOrderId}/comments/?expand=user&limit=10`
    if (cursor) {
      url += `&cursor=${cursor}`
    }

    const response = await axios.get(url)
    setLoading(false)
    setSuccess(response)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Creates a comment using the specified `payload`.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {string} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} setSuccess
 */
export const createComment = async (
  dealerId,
  repairOrderId,
  payload,
  setLoading = () => {},
  setError = () => {},
  setSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.post(
      `dealers/${dealerId}/repair-orders/${repairOrderId}/comments/?expand=user`,
      payload,
    )

    setLoading(false)
    setSuccess(response)
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Marks all comments as read for the current user on a repair order.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @returns {object|null} The response object on success, or `null` on error.
 */
export const markCommentsRead = async (dealerId, repairOrderId) => {
  try {
    const response = await axios.post(
      `/dealers/${dealerId}/repair-orders/${repairOrderId}/mark_comments_read/`,
    )
    return response
  } catch (err) {
    console.error('Error marking comments as read:', getErrorMessage(err))
    return null
  }
}

/**
 * Deletes a repair order.
 * @param {string} dealerId
 * @param {string} repairOrderId
 * @param {object} payload
 * @param {func} setLoading
 * @param {func} setError
 * @param {func} handleSuccess
 */
export const deleteRepairOrder = async (
  dealerId,
  repairOrderId,
  payload,
  setLoading = () => {},
  setError = () => {},
  handleSuccess = () => {},
) => {
  setLoading(true)

  try {
    const response = await axios.delete(`/dealers/${dealerId}/repair-orders/${repairOrderId}/`, {
      data: payload,
    })
    setLoading(false)
    handleSuccess()
    return response
  } catch (err) {
    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Gets the activity timeline for a repair order. URL-based so callers can pass
 * the paginated `next` link to load more.
 * @param {string} url - e.g. `/repair-orders/${id}/timeline/?limit=50`
 * @param {func} setLoading
 * @param {func} setError
 * @param {AbortSignal} signal
 */
export const getRepairOrderTimeline = async (
  url,
  setLoading = () => {},
  setError = () => {},
  signal = undefined,
) => {
  setLoading(true)

  try {
    const response = await axios.get(url, { signal })
    setLoading(false)
    return response
  } catch (err) {
    // Superseded by a newer request; leave loading state for the in-flight one.
    if (isCanceledError(err)) return null

    setError(getErrorMessage(err))
    setLoading(false)
    return null
  }
}

/**
 * Query params for the work queue endpoints: the dealer, then `filters`, an object of
 * param names to a value or a list of values (repeated in the URL). Empty values are
 * left out. The staff filters (`admin`, `assistant_manager`, `manager`) take several
 * names each (WARP-960).
 */
const workQueueParams = (dealerId, filters = {}) => {
  const params = new URLSearchParams()
  if (dealerId) params.append('dealer', dealerId)
  Object.entries(filters).forEach(([name, value]) => {
    ;[].concat(value ?? []).forEach((item) => {
      if (item !== '' && item !== null && item !== undefined) params.append(name, item)
    })
  })
  return params
}

/**
 * WARP-959 — one of the WPC work queues, oldest received first: `ready` (Not Started),
 * `closed`, or `admin_review` (Outstanding List ROs waiting on WPC). Across every dealer
 * the user may access; `dealerId` narrows it to one dealer, null means all of them.
 * @param {string|null} dealerId
 * @param {'ready'|'closed'|'admin_review'} queue
 * @param {func} setError
 * @param {AbortSignal} signal
 * @param {object} filters - see `workQueueParams`
 */
export const getWorkQueue = async (
  dealerId,
  queue,
  setError,
  signal = undefined,
  filters = {},
) => {
  const params = workQueueParams(dealerId, { queue, ...filters })
  try {
    return await axios.get(`/repair-orders/work-queue/?${params.toString()}`, { signal })
  } catch (err) {
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * WARP-960 — the work queue counts (see `getWorkQueueCounts`) for every dealer the user
 * can reach, one row each, idle dealers too: `{ ...totals, dealers: [{ id, name,
 * isRoIntakeEnabled, isDealerManaged, admin, assistantManager, manager, total, late,
 * oldestReceivedAt, queues }] }`. The all-dealers dashboard groups the rows by person.
 * @param {object} filters - see `workQueueParams`
 * @param {func} setError
 * @param {AbortSignal} signal
 */
export const getWorkQueueByDealer = async (filters, setError, signal = undefined) => {
  const params = workQueueParams(null, { ...filters, group_by: 'dealer' })
  try {
    return await axios.get(`/repair-orders/work-queue/counts/?${params.toString()}`, { signal })
  } catch (err) {
    if (isCanceledError(err)) return null
    setError(getErrorMessage(err))
    return null
  }
}

/**
 * The all-dealers dashboard's table as an .xlsx, downloaded at once: one row per dealer
 * in `dealerIds` (the rows on the page), with a Total row first.
 * @param {string[]} dealerIds
 * @param {func} setLoading
 * @param {func} setError
 */
export const downloadWorkloadExport = async (dealerIds, setLoading, setError) => {
  setLoading(true)
  try {
    const file = await axios.post(
      '/repair-orders/work-queue/counts/export/',
      { dealers: dealerIds },
      { responseType: 'blob' },
    )
    const url = window.URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = `Dealer Workload ${new Date().toLocaleDateString('en-CA')}.xlsx`
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  } catch (err) {
    setError(getErrorMessage(err))
  } finally {
    setLoading(false)
  }
}

/**
 * Every work queue's totals, for its tabs, summary cards, and the nav badge:
 * `{ total, late, oldestReceivedAt, queues: { ready, closed, adminReview, dealerReview,
 * pending }, slaHours, dueSoonHours, guidelineHours }`. Ready and Closed carry
 * `{ total, late, dueSoon }`; the two reviews carry `{ total, waiting, agedOut }`.
 * @param {string|null} dealerId
 * @param {AbortSignal} signal
 * @param {object} filters - see `workQueueParams`
 */
export const getWorkQueueCounts = async (dealerId, signal = undefined, filters = {}) => {
  const params = workQueueParams(dealerId, filters).toString()
  try {
    return await axios.get(`/repair-orders/work-queue/counts/${params ? `?${params}` : ''}`, {
      signal,
    })
  } catch (err) {
    // The badge is a hint, not a page; a failure here stays quiet.
    return null
  }
}
